import { jwtDecode } from 'jwt-decode';

/**
 * JWT Token Management Utility
 *
 * Provides secure storage, validation, and retrieval of JWT tokens.
 * Uses localStorage for persistent authentication across sessions.
 * Security is maintained through automatic JWT token expiration (1 hour default).
 *
 * Compatible with both JWT and Function Key authentication during migration period.
 */

// Storage key for JWT token (localStorage for persistent authentication)
const JWT_TOKEN_KEY = 'rt-jwt-token';

/**
 * Store JWT token in localStorage
 * @param {string} token - The JWT token string
 */
export const setJwtToken = (token) => {
  if (!token) {
    console.warn('[jwtService] Attempted to store null/empty token');
    return;
  }
  localStorage.setItem(JWT_TOKEN_KEY, token);
  console.log('[jwtService] JWT token stored in localStorage');
};

/**
 * Retrieve JWT token from localStorage
 * @returns {string|null} The JWT token or null if not found
 */
export const getJwtToken = () => {
  return localStorage.getItem(JWT_TOKEN_KEY);
};

/**
 * Remove JWT token from localStorage
 */
export const clearJwtToken = () => {
  localStorage.removeItem(JWT_TOKEN_KEY);
  console.log('[jwtService] JWT token cleared from localStorage');
};

/**
 * Decode JWT token without validation (for reading claims)
 * @param {string} token - Optional token to decode. If not provided, uses stored token.
 * @returns {object|null} Decoded token payload or null if invalid
 */
export const decodeToken = (token = null) => {
  const tokenToUse = token || getJwtToken();
  if (!tokenToUse) {
    return null;
  }

  try {
    const decoded = jwtDecode(tokenToUse);
    return decoded;
  } catch (error) {
    console.error('[jwtService] Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if the JWT token is expired or expiring soon
 * @param {number} thresholdMinutes - Minutes before expiration to consider "expired" (default: 5)
 * @returns {boolean} True if token is expired or expiring soon
 */
export const isTokenExpired = (thresholdMinutes = 5) => {
  const token = getJwtToken();
  if (!token) {
    return true; // No token means "expired"
  }

  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) {
      console.warn('[jwtService] Token has no expiration claim');
      return true; // No expiration claim means invalid token
    }

    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const threshold = thresholdMinutes * 60 * 1000; // Convert minutes to milliseconds

    const isExpiring = (expiresAt - now) < threshold;

    if (isExpiring) {
      const minutesRemaining = Math.floor((expiresAt - now) / 60000);
      console.log(`[jwtService] Token expires in ${minutesRemaining} minutes`);
    }

    return isExpiring;
  } catch (error) {
    console.error('[jwtService] Error checking token expiration:', error);
    return true; // Assume expired on error
  }
};

/**
 * Get the Authorization header for API calls
 * @returns {object} Header object with Authorization field, or empty object if no token
 *
 * Example usage:
 * fetch(url, {
 *   headers: {
 *     'Content-Type': 'application/json',
 *     ...getAuthHeader()
 *   }
 * })
 */
export const getAuthHeader = () => {
  const token = getJwtToken();
  if (!token) {
    return {}; // No token, return empty object (fallback to Function Key auth)
  }

  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Get user information from stored JWT token
 * @returns {object|null} User info object with userId, email, role, tenantId or null if no token
 */
export const getUserFromToken = () => {
  const decoded = decodeToken();
  if (!decoded) {
    return null;
  }

  return {
    userId: decoded.sub,
    email: decoded.email,
    role: decoded.role,
    tenantId: decoded.tenantId,
    expiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
    issuedAt: decoded.iat ? new Date(decoded.iat * 1000) : null
  };
};

/**
 * Check if user has a specific role
 * @param {string} requiredRole - The role to check (e.g., "Admin", "Host")
 * @returns {boolean} True if user has the required role
 */
export const hasRole = (requiredRole) => {
  const userInfo = getUserFromToken();
  if (!userInfo) {
    return false;
  }

  return userInfo.role === requiredRole;
};

/**
 * Get time remaining until token expiration
 * @returns {number|null} Minutes remaining until expiration, or null if no token
 */
export const getTimeUntilExpiration = () => {
  const token = getJwtToken();
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) {
      return null;
    }

    const expiresAt = decoded.exp * 1000;
    const now = Date.now();
    const remaining = expiresAt - now;

    return Math.floor(remaining / 60000); // Return minutes
  } catch (error) {
    console.error('[jwtService] Error calculating time until expiration:', error);
    return null;
  }
};

/**
 * Validate token format (basic check, not cryptographic validation)
 * @param {string} token - Token to validate
 * @returns {boolean} True if token has valid JWT format (3 parts separated by dots)
 */
export const isValidJwtFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT format: header.payload.signature
  const parts = token.split('.');
  return parts.length === 3;
};

/**
 * Refresh the JWT token before it expires.
 * Calls the backend RefreshToken endpoint to get a new token.
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const refreshToken = async () => {
  const currentToken = getJwtToken();
  if (!currentToken) {
    return { success: false, error: 'No token to refresh' };
  }

  try {
    const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';
    const response = await fetch(`${functionsUrl}/RefreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[jwtService] Token refresh failed:', response.status, errorData);
      return { success: false, error: errorData.error || `Refresh failed with status ${response.status}` };
    }

    const data = await response.json();

    if (data.JwtToken) {
      // Store the new token
      setJwtToken(data.JwtToken);
      console.log('[jwtService] Token refreshed successfully, new expiration in', data.ExpiresIn, 'seconds');
      return { success: true, token: data.JwtToken, user: data.User };
    } else {
      console.error('[jwtService] Refresh response missing JwtToken');
      return { success: false, error: 'Invalid refresh response' };
    }
  } catch (error) {
    console.error('[jwtService] Error refreshing token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Debug utility: Log token information to console
 * WARNING: Only use in development! Do not use in production.
 */
export const debugToken = () => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('[jwtService] debugToken() should not be used in production');
    return;
  }

  const token = getJwtToken();
  if (!token) {
    console.log('[jwtService] No token stored');
    return;
  }

  console.log('[jwtService] Token present:', isValidJwtFormat(token));
  console.log('[jwtService] Token expired:', isTokenExpired());
  console.log('[jwtService] Time until expiration:', getTimeUntilExpiration(), 'minutes');
  console.log('[jwtService] User info:', getUserFromToken());
  console.log('[jwtService] Decoded token:', decodeToken());
};
